import { useState, useEffect } from 'react';
import { collection, query, onSnapshot, doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebaseConfig';

export interface ProductCategory {
  id: string;
  name: string;
  description?: string;
  order?: number;
}

export interface ProductVariant {
  id: string;
  name: string; // e.g., "16GB DDR4 3200MHz"
  price?: number;
  priceType?: 'fixed' | 'range';
  priceRange?: { min: number; max: number };
  baseStock: string;
  stockType?: 'in-stock' | 'order-basis';
  sku?: string; // Optional SKU for inventory tracking
  image?: string; // Optional image URL for the variant
}

export interface Product {
  id: string;
  inventoryId?: string;
  name: string;
  description: string;
  categoryId: string;
  categoryName?: string; // Populated from published_categories
  brand: string;
  price?: number;
  priceType?: 'fixed' | 'range'; // New: Fixed price or price range
  priceRange?: { min: number; max: number }; // New: For ranged pricing
  baseStock: string; // Changed from number to string to support "Order Basis"
  stockType?: 'in-stock' | 'order-basis'; // New: Stock availability type
  hasVariants: boolean; // New: Whether product has variants
  variants?: ProductVariant[]; // New: Product variants
  images: string[];
  specifications: {
    [key: string]: string;
  };
  isActive?: boolean;
  isPublished?: boolean;
  createdAt: any;
  updatedAt: any;
  order?: number;
}

// Hook to fetch all product categories
export const useProductCategories = () => {
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    try {
      const categoriesRef = collection(db, 'product_categories');
      // Simple query without orderBy to avoid index requirements
      const q = query(categoriesRef);

      const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          const categoriesData = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          })) as ProductCategory[];
          
          // Sort by order field in JavaScript (or alphabetically if no order)
          categoriesData.sort((a, b) => {
            if (a.order !== undefined && b.order !== undefined) {
              return a.order - b.order;
            }
            return (a.name || '').localeCompare(b.name || '');
          });
          
          console.log('Categories loaded:', categoriesData.length, categoriesData);
          setCategories(categoriesData);
          setLoading(false);
        },
        (err) => {
          console.error('Error fetching categories:', err);
          setError('Failed to load categories');
          setLoading(false);
        }
      );

      return () => unsubscribe();
    } catch (err) {
      console.error('Error setting up categories listener:', err);
      setError('Failed to initialize categories');
      setLoading(false);
    }
  }, []);

  return { categories, loading, error };
};

// Hook to fetch products from inventory collection (for admin)
export const useInventoryProducts = (categoryIdFilter?: string, activeOnly: boolean = true) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const inventoryRef = collection(db, 'inventory');
        // Simple query without compound filters to avoid index requirements
        const q = query(inventoryRef);

        const unsubscribe = onSnapshot(
          q,
          async (snapshot) => {
            // Fetch category names for all products
            let productsWithCategories = await Promise.all(
              snapshot.docs.map(async (productDoc) => {
                const productData = productDoc.data();
                let categoryName = 'Unknown';

                // Fetch category name from product_categories
                if (productData.categoryId) {
                  try {
                    const categoryDoc = await getDoc(doc(db, 'product_categories', productData.categoryId));
                    if (categoryDoc.exists()) {
                      categoryName = categoryDoc.data().name || 'Unknown';
                    }
                  } catch (err) {
                    console.error('Error fetching category:', err);
                  }
                }

                return {
                  id: productDoc.id,
                  ...productData,
                  categoryName,
                } as Product;
              })
            );

            // Filter in JavaScript instead of Firestore query
            if (activeOnly) {
              productsWithCategories = productsWithCategories.filter(p => p.isActive === true);
            }

            if (categoryIdFilter) {
              productsWithCategories = productsWithCategories.filter(p => p.categoryId === categoryIdFilter);
            }

            // Sort by createdAt descending (newest first)
            productsWithCategories.sort((a, b) => {
              const aTime = a.createdAt?.toMillis?.() || 0;
              const bTime = b.createdAt?.toMillis?.() || 0;
              return bTime - aTime;
            });

            setProducts(productsWithCategories);
            setLoading(false);
          },
          (err) => {
            console.error('Error fetching products:', err);
            setError('Failed to load products');
            setLoading(false);
          }
        );

        return () => unsubscribe();
      } catch (err) {
        console.error('Error setting up products listener:', err);
        setError('Failed to initialize products');
        setLoading(false);
      }
    };

    fetchProducts();
  }, [categoryIdFilter, activeOnly]);

  return { products, loading, error };
};

// Hook to fetch PUBLISHED products (for public website)
export const usePublishedProducts = (categoryIdFilter?: string) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPublishedProducts = async () => {
      try {
        const publishedRef = collection(db, 'published_products');
        const q = query(publishedRef);

        const unsubscribe = onSnapshot(
          q,
          async (snapshot) => {
            // Fetch category names for all products
            let productsWithCategories = await Promise.all(
              snapshot.docs.map(async (productDoc) => {
                const productData = productDoc.data();
                let categoryName = 'Unknown';

                // Fetch category name from published_categories
                if (productData.categoryId) {
                  try {
                    const categoryDoc = await getDoc(doc(db, 'published_categories', productData.categoryId));
                    if (categoryDoc.exists()) {
                      categoryName = categoryDoc.data().name || 'Unknown';
                    }
                  } catch (err) {
                    console.error('Error fetching category:', err);
                  }
                }

                return {
                  id: productDoc.id,
                  ...productData,
                  categoryName,
                } as Product;
              })
            );

            // Filter only published products
            productsWithCategories = productsWithCategories.filter(p => p.isPublished === true);

            // Filter by category if specified
            if (categoryIdFilter) {
              productsWithCategories = productsWithCategories.filter(p => p.categoryId === categoryIdFilter);
            }

            // Sort by createdAt descending (newest first)
            productsWithCategories.sort((a, b) => {
              const aTime = a.createdAt?.toMillis?.() || 0;
              const bTime = b.createdAt?.toMillis?.() || 0;
              return bTime - aTime;
            });

            setProducts(productsWithCategories);
            setLoading(false);
          },
          (err) => {
            console.error('Error fetching published products:', err);
            setError('Failed to load products');
            setLoading(false);
          }
        );

        return () => unsubscribe();
      } catch (err) {
        console.error('Error setting up published products listener:', err);
        setError('Failed to initialize products');
        setLoading(false);
      }
    };

    fetchPublishedProducts();
  }, [categoryIdFilter]);

  return { products, loading, error };
};

// Hook to fetch categories that are actually used in published products
export const usePublishedProductCategories = () => {
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPublishedCategories = async () => {
      try {
        const publishedRef = collection(db, 'published_products');
        const q = query(publishedRef);

        const unsubscribe = onSnapshot(
          q,
          async (snapshot) => {
            // Get unique category IDs from published products that are published
            const publishedProducts = snapshot.docs
              .map(doc => doc.data())
              .filter(product => product.isPublished === true);
            
            const uniqueCategoryIds = [...new Set(publishedProducts.map(p => p.categoryId).filter(Boolean))];

            // Fetch category details for each unique category ID
            const categoriesData = await Promise.all(
              uniqueCategoryIds.map(async (categoryId) => {
                try {
                  const categoryDoc = await getDoc(doc(db, 'product_categories', categoryId as string));
                  if (categoryDoc.exists()) {
                    return {
                      id: categoryDoc.id,
                      ...categoryDoc.data(),
                    } as ProductCategory;
                  }
                  return null;
                } catch (err) {
                  console.error(`Error fetching category ${categoryId}:`, err);
                  return null;
                }
              })
            );

            // Filter out nulls and sort
            const validCategories = categoriesData.filter(cat => cat !== null) as ProductCategory[];
            validCategories.sort((a, b) => {
              if (a.order !== undefined && b.order !== undefined) {
                return a.order - b.order;
              }
              return (a.name || '').localeCompare(b.name || '');
            });

            console.log('Published product categories loaded:', validCategories.length, validCategories);
            setCategories(validCategories);
            setLoading(false);
          },
          (err) => {
            console.error('Error fetching published categories:', err);
            setError('Failed to load categories');
            setLoading(false);
          }
        );

        return () => unsubscribe();
      } catch (err) {
        console.error('Error setting up published categories listener:', err);
        setError('Failed to initialize categories');
        setLoading(false);
      }
    };

    fetchPublishedCategories();
  }, []);

  return { categories, loading, error };
};

// Backward compatibility - defaults to published products for public pages
export const useProducts = usePublishedProducts;
